import React from 'react';
import {
    Box,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Chip,
    FormHelperText
} from '@mui/material';

import KeyValueEditor from './components/KeyValueEditor';
import {
    asFlatStringObject,
    rowsToObject,
    toRawText,
    toRows,
    newRow,
    sameDocument
} from '../../utils/jsonObjectValue';
import i18n from '../../i18n';

/**
 * JSON object input, with a named-field editor over the top of the raw text.
 *
 * Two modes. **Fields** is the default and shows the object as key/value rows, so
 * an operator entering card or IBAN requisites never sees a brace. **Raw** is the
 * same monospace JSON textarea as before, for anything the rows cannot express.
 *
 * Fields mode deliberately handles only a flat object of **string** values, and
 * falls back to raw with a visible reason otherwise. That is not laziness about
 * nesting — it is about not corrupting data. Values here are things like card
 * numbers and IBANs, and inferring types from text would turn
 * "4111111111111111" into a float and silently lose digits, while a leading zero
 * on an account number would vanish. Strings in, strings out; anything with a
 * number, boolean, array or nested object in it is raw-only, and typed values can
 * still be written there.
 *
 * Invalid raw input is reported upward as-is rather than swallowed, the same
 * contract AmountInput uses — the `Json` rule in utils/fieldValidation turns it
 * into a visible error with Save disabled, so the form state never diverges from
 * what is on screen.
 *
 * A subclass can offer further modes over the same value — extraModes(),
 * renderModeBody(), helperLine() — and emit through emitValue(), which keeps the
 * raw text in step. DialogScraperSettingsJsonField adds one this way;
 * DialogWalletRequisitesField adds one and, where it applies, takes Fields away
 * (fieldsModeOffered) and chooses the preset chips (presets).
 */
export default class DialogJsonField extends React.Component {
    constructor(props) {
        super(props);
        const flat = asFlatStringObject(props.value);
        this.state = {
            mode: flat ? 'fields' : 'raw',
            rows: flat ? toRows(flat) : [],
            rawText: toRawText(props.value),
            lastEmitted: props.value
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        // Only react to a value that did not come from us — a reset, a template,
        // or a different record. Compared by content: RHF's Controller returns a
        // deep clone, so a reference check is false even for a value this field
        // emitted a moment ago, and acting on that rebuilt the rows (and stole
        // focus) on every keystroke.
        if (sameDocument(nextProps.value, prevState.lastEmitted)) return null;

        const flat = asFlatStringObject(nextProps.value);
        return {
            mode: flat ? prevState.mode : 'raw',
            // Existing ids are carried over, so untouched inputs are not remounted.
            rows: flat ? toRows(flat, prevState.rows) : prevState.rows,
            rawText: toRawText(nextProps.value),
            lastEmitted: nextProps.value
        };
    }

    emit = (value) => {
        this.setState({ lastEmitted: value });
        this.props.onChange(this.props.field.id, value);
    };

    emitRows = (rows) => {
        const obj = rowsToObject(rows);
        this.setState({ rows, rawText: toRawText(obj) });
        // An object with no usable rows is absent, not {} — that lets `required`
        // do its job and keeps an optional field out of the payload.
        this.emit(Object.keys(obj).length ? obj : null);
    };

    handleRowChange = (id, which, text) => {
        this.emitRows(this.state.rows.map((r) => (r.id === id ? { ...r, [which]: text } : r)));
    };

    handleRemoveRow = (id) => {
        this.emitRows(this.state.rows.filter((r) => r.id !== id));
    };

    handleAddRow = () => {
        this.setState(
            (s) => ({ rows: [...s.rows, newRow()] }),
            () => this.emitRows(this.state.rows)
        );
    };

    handleRawChange = (event) => {
        const raw = event.target.value;
        this.setState({ rawText: raw });

        if (raw.trim() === '') {
            this.emit(null);
            return;
        }
        try {
            this.emit(JSON.parse(raw));
        } catch {
            // Reported as-is; the Json validation rule turns this into the error.
            this.emit(raw);
        }
    };

    handleModeChange = (_event, mode) => {
        if (!mode || mode === this.state.mode) return;

        if (mode === 'fields') {
            const flat = asFlatStringObject(this.props.value);
            if (!flat) return; // guarded by the disabled toggle, belt and braces
            this.setState({ mode, rows: toRows(flat, this.state.rows) });
            return;
        }
        this.setState({ mode, rawText: toRawText(this.props.value) });
    };

    /**
     * Emit a whole new value from a mode of a subclass, with the raw text
     * following it so a switch to Raw shows what was just done.
     */
    emitValue = (value) => {
        this.setState({ rawText: toRawText(value) });
        this.emit(value);
    };

    /**
     * Modes offered after Fields and Raw: `[{ value, label, disabled }]`. None
     * here; a subclass names its own and renders them in renderModeBody.
     */
    extraModes() {
        return [];
    }

    /** The body of a mode from extraModes(). */
    renderModeBody(mode) {
        return null;
    }

    /** Whether Fields is on offer at all; a subclass's own mode may replace it. */
    fieldsModeOffered() {
        return true;
    }

    /** The chips that prefill key names in Fields mode: `[{ label, keys }]`. */
    presets() {
        return this.props.field.jsonPresets || [];
    }

    /** The line under the editor, or '' for none. */
    helperLine(mode, fieldsPossible) {
        const { field, error } = this.props;
        const helper = field.helperText ? i18n.t(`helperTexts.${field.helperText}`) : '';
        return error || (!fieldsPossible ? i18n.t('text.jsonRawOnlyReason') : helper);
    }

    applyPreset = (keys) => {
        const existing = rowsToObject(this.state.rows);
        const merged = [...this.state.rows.filter((r) => r.key.trim() !== '')];
        keys.forEach((key) => {
            if (!(key in existing)) merged.push(newRow(key));
        });
        this.emitRows(merged);
    };

    render() {
        const { field, error } = this.props;
        const { mode, rows, rawText } = this.state;

        const disabled = Boolean(field.onlyRead);
        const fieldsPossible = asFlatStringObject(this.props.value) !== null;
        const presets = this.presets();
        const helper = this.helperLine(mode, fieldsPossible);

        return (
            <Box>
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                >
                    <Typography variant="subtitle2" color={error ? 'error' : 'text.primary'}>
                        {i18n.t(`columns.${field.label}`)}
                        {field.required ? ' *' : ''}
                    </Typography>

                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={mode}
                        onChange={this.handleModeChange}
                    >
                        {this.fieldsModeOffered() && (
                            <ToggleButton value="fields" disabled={disabled || !fieldsPossible}>
                                {i18n.t('text.jsonModeFields')}
                            </ToggleButton>
                        )}
                        <ToggleButton value="raw" disabled={disabled}>
                            {i18n.t('text.jsonModeRaw')}
                        </ToggleButton>
                        {this.extraModes().map((extra) => (
                            <ToggleButton
                                key={extra.value}
                                value={extra.value}
                                disabled={disabled || Boolean(extra.disabled)}
                            >
                                {extra.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Stack>

                {mode === 'fields' && presets.length > 0 && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">
                            {i18n.t('text.jsonPreset')}
                        </Typography>
                        {presets.map((preset) => (
                            <Chip
                                key={preset.label}
                                size="small"
                                variant="outlined"
                                clickable={!disabled}
                                disabled={disabled}
                                label={i18n.t(`jsonPresets.${preset.label}`)}
                                onClick={() => this.applyPreset(preset.keys)}
                            />
                        ))}
                    </Stack>
                )}

                {mode === 'fields' && (
                    <KeyValueEditor
                        rows={rows}
                        disabled={disabled}
                        onRowChange={this.handleRowChange}
                        onRemoveRow={this.handleRemoveRow}
                        onAddRow={this.handleAddRow}
                    />
                )}
                {mode === 'raw' && (
                    <TextField
                        id={field.id}
                        error={Boolean(error)}
                        value={rawText}
                        disabled={disabled}
                        fullWidth
                        multiline
                        minRows={4}
                        maxRows={14}
                        onBlur={this.props.onBlur}
                        onChange={this.handleRawChange}
                        InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                    />
                )}
                {mode !== 'fields' && mode !== 'raw' && this.renderModeBody(mode)}

                {helper && (
                    <FormHelperText error={Boolean(error)}>{helper}</FormHelperText>
                )}
            </Box>
        );
    }
}
